import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { Tip } from "../../../src/types/contents/tip";
import { createTip, getTip, updateTip, uploadMedia } from "../api";
import { auth } from "../auth";

import { MediaWithPreview, TagsSelector } from "./Common";

const emptyTip: Tip<"api"> = {
  title: "",
  category: "",
  language: "",
  contents: [
    {
      subtitle: "",
      bodyText: "",
    },
  ],
  media: "",
  favorite: false,
  sportTitle: "",
  tags: [],
};

export const TipComponent = () => {
  const [tip, setTip] = useState<Tip<"api"> | null>(null);
  const [mediaPreview, setMediaPreview] = useState<File | null>(null);
  const [mediaIdOverride, setMediaIdOverride] = useState<boolean>(false);

  const { id } = useParams();
  const isEdit = id !== undefined && id !== "new";
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      if (isEdit) {
        setTip(await getTip(id));
      } else {
        setTip({
          ...emptyTip,
        });
      }
    };

    if (auth.currentUser) void init();
  }, []);

  if (tip === null) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4">Loading...</Typography>
      </Stack>
    );
  }

  const upsertMedia = async () => {
    if (mediaPreview) {
      const uploadResult = await uploadMedia(
        mediaPreview,
        mediaIdOverride ? tip.media : undefined,
      );
      setMediaPreview(null);
      if (uploadResult.public_id) {
        return uploadResult.public_id;
      }
    }
    return tip.media;
  };

  const save = async () => {
    try {
      const media = await upsertMedia();
      const editedTip = {
        ...tip,
        media,
      };
      if (isEdit) {
        await updateTip(editedTip);
      } else {
        const { id: newId } = await createTip(editedTip);
        if (newId) {
          navigate(`/tip/${newId}`);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        alert(e.message);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Title"
        value={tip.title}
        variant="outlined"
        onChange={(input) => {
          setTip({ ...tip, title: input.currentTarget.value });
        }}
      />
      <TextField
        label="Category"
        value={tip.category}
        variant="outlined"
        onChange={(input) => {
          setTip({
            ...tip,
            category: input.currentTarget.value,
          });
        }}
      />
      <TextField
        label="Language"
        value={tip.language}
        variant="outlined"
        onChange={(input) => {
          setTip({ ...tip, language: input.currentTarget.value });
        }}
      />
      <Typography variant="subtitle1">Tip contents</Typography>
      {tip.contents.map((content, index) => {
        return (
          <Stack key={index} spacing={2}>
            <TextField
              label="Subtitle"
              value={content.subtitle}
              variant="outlined"
              onChange={(input) => {
                const newContents = [...tip.contents];
                newContents[index].subtitle = input.currentTarget.value;
                setTip({ ...tip, contents: newContents });
              }}
            />
            <TextField
              label="Body text"
              value={content.bodyText}
              variant="outlined"
              onChange={(input) => {
                const newContents = [...tip.contents];
                newContents[index].bodyText = input.currentTarget.value;
                setTip({ ...tip, contents: newContents });
              }}
            />
          </Stack>
        );
      })}
      <Button
        component="label"
        variant="contained"
        onClick={() => {
          const newContents = [...tip.contents, { subtitle: "", bodyText: "" }];
          setTip({ ...tip, contents: newContents });
        }}
      >
        Add content
      </Button>
      <MediaWithPreview
        type="image"
        label="Upload media"
        alt="Tip media"
        mediaId={tip.media}
        mediaPreview={mediaPreview}
        setMediaPreview={setMediaPreview}
        mediaIdOverride={mediaIdOverride}
        setMediaIdOverride={setMediaIdOverride}
        overrideAble={isEdit}
      />
      <TextField
        label="Sport title"
        value={tip.sportTitle}
        variant="outlined"
        onChange={(input) => {
          setTip({ ...tip, sportTitle: input.currentTarget.value });
        }}
      />
      <Stack direction="row" spacing={2}>
        <TagsSelector
          value={tip.tags ?? []}
          onChange={(selected) => {
            setTip({
              ...tip,
              tags: selected,
            });
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={tip.favorite}
              onChange={(e) => setTip({ ...tip, favorite: e.target.checked })}
            />
          }
          label="Favorite"
        />
      </Stack>
      <Button variant="contained" onClick={() => save()}>
        {tip.id ? "Save" : "Create"}
      </Button>
    </Stack>
  );
};
